import { getRecordById } from '../recordStorageService';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';

const CSAUDIT_OBJECT = 'CSAudit__c';
const CUSTOM_SUFFIX = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';

/** Max concurrent `getRecordById` calls when hydrating traversal-sparse CSAudit rows. */
const CSAUDIT_TRAVERSAL_HYDRATE_CONCURRENCY = 12;

function csAuditRecordHasPhaseStatusForUi(r: Record<string, unknown>): boolean {
  const data = (r.data as Record<string, unknown>) ?? {};
  const top = r as Record<string, unknown>;
  const keys = [
    'phaseStatus',
    'Phase_Status__c',
    'PhaseStatus__c',
    'custom__phase_status',
    `Phase_Status${CUSTOM_SUFFIX}`,
  ];
  for (const k of keys) {
    const v = top[k] ?? data[k];
    if (v != null && String(v).trim() !== '') return true;
  }
  return false;
}

async function hydrateOneCsAuditRecordFromObjectApi(r: Record<string, unknown>): Promise<Record<string, unknown>> {
  if (csAuditRecordHasPhaseStatusForUi(r)) return r;
  const id = String(r.id ?? '').trim();
  if (!/^[0-9]+$/.test(id)) return r;
  try {
    const full = await getRecordById(CSAUDIT_OBJECT, id);
    if (!full) return r;
    const merged = { ...r, ...(full as Record<string, unknown>) };
    const rel = (r as { relatedClientId?: string }).relatedClientId;
    if (rel && !(merged as { relatedClientId?: string }).relatedClientId) {
      (merged as { relatedClientId?: string }).relatedClientId = rel;
    }
    return merged;
  } catch {
    return r;
  }
}

/**
 * Graph `traverseObjects` often returns CSAudit nodes without full property payloads (id/key/label only).
 * List and board UIs read `Phase_Status__c` / `custom__phase_status` for badges — re-fetch by Gobodo id when those are missing.
 */
export async function hydrateCSAuditRecordsForListUi(
  records: Record<string, unknown>[]
): Promise<Record<string, unknown>[]> {
  if (!USE_GOBODO_API || records.length === 0) return records;

  const out: Record<string, unknown>[] = [];
  for (let i = 0; i < records.length; i += CSAUDIT_TRAVERSAL_HYDRATE_CONCURRENCY) {
    const slice = records.slice(i, i + CSAUDIT_TRAVERSAL_HYDRATE_CONCURRENCY);
    const chunk = await Promise.all(slice.map((r) => hydrateOneCsAuditRecordFromObjectApi(r)));
    out.push(...chunk);
  }
  if (process.env.NODE_ENV === 'development') {
    const sparseIn = records.filter((r) => !csAuditRecordHasPhaseStatusForUi(r)).length;
    if (sparseIn > 0) {
      const stillSparse = out.filter((r) => !csAuditRecordHasPhaseStatusForUi(r)).length;
      console.log(
        `[CyberSweep] CSAudit hydration: ${sparseIn} sparse traversal row(s); ${stillSparse} still without phase status after object GET`
      );
    }
  }
  return out;
}
